import { Form, Formik } from 'formik';
import { Fragment, useMemo, useRef, useState } from 'react';
import { buildFormData, scrollToError } from '../../../utils/HelperFunctions';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ISingleOrderEntity, IStepData } from '../types/interfaces';
import SingleOrderEntity from './SingleOrderEntity';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandAccordion from '../../products/components/productForm/ExpandAccordion';
import Button from '../../../components/buttons/Button';
import { generalCreate } from '../../../API/api';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import { lockIcon } from '../../../config/variables';
import ModalContainer from '../../../components/ModalContainer';
import SingleOrderReject from './SingleOrderReject';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmModal from '../../../components/ConfirmModal';

const SingleOrderStep = ({
  data,
  index,
  currentStepOrder,
  expanded,
  setExpanded,
  orderCancelled,
  orderedRefused
}: {
  data: IStepData;
  index: number;
  currentStepOrder: number;
  expanded: number;
  setExpanded: React.Dispatch<React.SetStateAction<number>>;
  orderCancelled?: boolean;
  orderedRefused?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const queryClient = useQueryClient();

  const { apiData } = useFormsIntegrationHelpers({
    queryKey: ['orders', id],
    id,
    singleGetApi: `/admin/orders/${id}`
  });

  const initialValues = useMemo(() => {
    if (!data) return {};
    type IEntities = Record<string, { entity_id: string; values: Record<string, { key: string; id: string; value: string }> }>;
    const entities: IEntities = {};

    data?.entities?.forEach((entity) => {
      const entity_id = entity?.id;
      const values: Record<string, { key: string; id: string; value: string }> = {};
      entity?.fields?.forEach((field) => {
        const key = field?.name;
        const id = field?.id;
        let value = field?.value || '';
        if (field.type === 'gallery') {
          value = (field?.value || [])?.map((ele: any) => ele.url);
        }
        if (field?.type === 'file' || field?.type === 'document') {
          value = (field?.value || [])?.[0]?.url;
        }
        if (field?.type === 'boolean') {
          value = value ? 1 : 0;
        }
        values[`field_${id}`] = { key, id: String(id), value };
      });
      entities[`entity_${entity_id}`] = { entity_id, values };
    });

    return { entities };
  }, [data]);

  function handleSubmit(values: any) {
    const newValues: any = {};
    setLoading(true);
    Object.keys(values?.entities as any).forEach((key: any) => {
      const entity: any = (values?.entities as any)[key];
      const entityObj = data?.entities?.find((ele) => ele.id === entity?.entity_id);
      if (entityObj?.editable_by_customer || entityObj?.auto_generate_documents) return;
      Object.keys(entity.values as any).forEach((key: any) => {
        const field: any = (entity.values as any)[key];
        if (field?.value || field?.value === 0) newValues[`field_values[${field.id}]`] = field.value;
      });
    });

    let route = `/admin/orders/${id}/steps/save-values`;
    const formData = new FormData();
    buildFormData(formData, newValues, '');
    generalCreate({ route, values: formData })
      .then(() => {
        toast.success(`${t('step')} ${t('updated')} ${t('successfully')}`);
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['steps'] });
      })
      .catch((error) => {
        const obj = error?.response?.data?.data;
        const errType = typeof obj;
        if (errType === 'object') {
          Object?.keys(obj)?.map((item: any) => toast.error(obj[item][0]));
        } else {
          toast.error(obj);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const { handleSubmit: handleApproveSubmit, submitLoading } = useFormsIntegrationHelpers({
    addApi: `/admin/orders/${id}/steps/submit`,
    invalidateQueryKey: ['orders'],
    customMessage: `${t('step')} ${t('updated')} ${t('successfully')}`,
    noScroll: true,
    onComplete: () => {
      setRejectOpen(false);
      setAcceptOpen(false);
      queryClient.invalidateQueries({ queryKey: ['steps'] });
    }
  });

  function generateYupSchema(entities: ISingleOrderEntity[]) {
    const schema: any = {};

    entities.forEach((entity) => {
      const entitySchema: any = {};
      if (entity?.editable_by_customer) return;

      entity?.fields.forEach((field) => {
        const { validation } = field;
        let validationRule: any;

        if (validation?.required)
          validationRule = (field?.type === 'file' || field?.type === 'gallery' || field?.type === 'image' ? Yup.mixed() : Yup.string()).required('This field is required');
        if (validation?.options) validationRule = (validationRule || Yup.string()).oneOf(validation?.options).required('This field is required');

        if (validation?.min && validation?.max && field?.type !== 'gallery')
          validationRule = (validationRule || Yup.string())
            .min(validation.min, `Minimum ${validation.min} characters required`)
            .max(validation.max, `Maximum ${validation.max} characters allowed`);

        if (field?.type === 'gallery') validationRule = Yup.array().of(validationRule).min(1, t('at_least_one_image'));

        if (validationRule) entitySchema[`field_${field.id}`] = Yup.object().shape({ value: validationRule });
      });

      schema[`entity_${entity?.id}`] = Yup.object().shape({
        values: Yup.object().shape(entitySchema)
      });
    });

    return Yup.object().shape({
      entities: Yup.object().shape(schema)
    });
  }

  const { orderFinished, canUpdateStepValues, canApproveStep, valuesSubmitted, haveUnresolvedComments } = useMemo(() => {
    const orderFinished = apiData?.status === 'Paid And Finished';

    const canUpdateStepValues = !!(data?.current_step && data?.entities?.find((entity) => !entity?.auto_generate_documents && !entity?.editable_by_customer)) && !orderFinished && !orderedRefused;

    const canApproveStep =
      !!(data?.current_step && data?.entities?.find((entity) => entity?.editable_by_customer)) &&
      !data?.entities?.find((entity) => entity?.editable_by_customer && entity.fields?.some((field) => !field?.value && field?.validation?.required)) &&
      !data?.customer_responsible &&
      !orderFinished && !orderedRefused;

    const haveUnresolvedComments = !!data?.entities?.find(
      (entity) => entity.editable_by_customer && entity?.fields?.some((field) => !field.approved && field?.comments?.find((comment) => !comment?.is_resolved))
    );

    let valuesSubmitted = false;
    data?.entities?.forEach((entity) => {
      if (entity?.editable_by_customer || entity?.auto_generate_documents) return;
      entity?.fields?.forEach((field) => {
        let fieldHasValue = false;
        if (field?.type === 'gallery') {
          fieldHasValue = !!field?.value?.length;
        }
        if (field?.type === 'file') {
          fieldHasValue = !!field?.value?.[0]?.url;
        } else {
          fieldHasValue = !!field?.value;
        }
        if (fieldHasValue) valuesSubmitted = true;
      });
    });

    return {
      canUpdateStepValues,
      canApproveStep,
      orderFinished,
      valuesSubmitted,
      haveUnresolvedComments
    };
  }, [data, apiData]);

  return (
    <div ref={formRef} className="single-step">
      <Formik
        enableReinitialize
        validateOnMount
        validationSchema={generateYupSchema(data?.entities || [])}
        initialValues={initialValues}
        onSubmit={(values) => {
          handleSubmit({ ...values });
        }}
      >
        {(formik) => (
          <Form>
            <Accordion
              expanded={expanded === data?.id}
              onChange={(e) => {
                e.stopPropagation();
                setExpanded(expanded === data?.id ? -1 : data?.id);
              }}
            >
              <AccordionSummary
                sx={{
                  '&.Mui-focusVisible': {
                    backgroundColor: '#000'
                  }
                }}
                aria-controls="panel1a-content"
                id="panel1a-header"
                className={`header-container ${expanded !== data?.id && 'expand'}`}
                disabled={(data?.order > currentStepOrder || !currentStepOrder || orderCancelled) && !orderFinished && !orderedRefused}
              >
                <div className="header-content" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span>
                    {(data?.order > currentStepOrder || !currentStepOrder || orderCancelled) && !orderFinished && !orderedRefused ? lockIcon : <ExpandAccordion expand={expanded === data?.id} />}
                  </span>
                  <span>
                    {index + 1}. {typeof data?.name == 'string' ? data?.name : data?.name[i18n.language as 'en' | 'ar']}
                  </span>
                  {(currentStepOrder || orderFinished) && !orderCancelled && (
                    <span
                      className={`badge ${data?.order_step_status === 'rejected' ? 'cancelled' : data?.order < currentStepOrder || orderFinished ? 'completed' : data?.order === currentStepOrder ? 'current' : 'pending'}`}
                    >
                      {data?.order_step_status === 'rejected'
                        ? t('rejected')
                        : data?.order < currentStepOrder || orderFinished
                          ? t('completed')
                          : data?.order === currentStepOrder
                            ? t('current')
                            : t('pending')}
                    </span>
                  )}
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  {data?.entities?.map((item: any, index: number) => (
                    <SingleOrderEntity
                      key={`step-entity-${index}`}
                      canUpdateStepValues={canUpdateStepValues}
                      canApproveStep={canApproveStep}
                      data={item}
                      formik={formik}
                      index={index}
                      stepData={data}
                    />
                  ))}
                </div>
                {((canApproveStep && !haveUnresolvedComments) || canUpdateStepValues) && !orderFinished && (
                  <div className={`form_button reverse`} style={{ marginTop: '2rem', justifyContent: 'flex-start', gap: '1em' }}>
                    {canUpdateStepValues && (
                      <Button
                        loading={loading}
                        disabled={loading || !formik?.dirty || !formik?.isValid}
                        text={valuesSubmitted ? t('update') : t('submit')}
                        onClick={() => {
                          scrollToError(!formik.isValid, formRef);
                        }}
                        type={'submit'}
                      />
                    )}
                    {canApproveStep && !haveUnresolvedComments && (
                      <Fragment>
                        <Button
                          loading={submitLoading}
                          disabled={submitLoading}
                          onClick={() => {
                            setAcceptOpen(true);
                          }}
                          text={t('accept')}
                          type={'button'}
                        />
                        {!!data?.entities?.flatMap((ele) => (ele?.auto_generate_documents || !ele?.editable_by_customer ? [] : ele.fields))?.length && (
                          <Button
                            disabled={submitLoading}
                            onClick={() => {
                              setRejectOpen(true);
                            }}
                            text={t('reject')}
                            type={'button'}
                          />
                        )}
                      </Fragment>
                    )}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          </Form>
        )}
      </Formik>
      {rejectOpen && (
        <ModalContainer small>
          <div className="common-modal">
            <SingleOrderReject data={data} setRejectOpen={setRejectOpen} loading={submitLoading} submitSubmission={(values: any) => handleApproveSubmit(values)} />
          </div>
        </ModalContainer>
      )}
      {acceptOpen && (
        <ModalContainer small>
          <ConfirmModal
            closeModal={() => setAcceptOpen(false)}
            queryKey={'orders'}
            message={t('are_you_sure_you_want_to_approve_this_step')}
            confirmFunc={() => {
              handleApproveSubmit({ status: 'approved' });
            }}
            successMsg={`${t('step_approved')} ${t('successfully')}`}
          />
        </ModalContainer>
      )}
    </div>
  );
};

export default SingleOrderStep;
